import { useState } from "react";
import { Logo } from "./Logo";
import type { Alert, ClusterSnapshot } from "../../types";

type Status = "connecting" | "live" | "polling" | "error" | "demo";

interface Props {
  view: "overview" | "node" | "job";
  onView: (v: "overview" | "node" | "job") => void;
  timeRange: string;
  setTimeRange: (v: string) => void;
  alerts: Alert[];
  onBell: () => void;
  crumbs?: string | null;
  snapshot: ClusterSnapshot | null;
  status: Status;
}

export function TopNav({
  view,
  onView,
  timeRange,
  setTimeRange,
  alerts,
  onBell,
  crumbs,
  snapshot,
  status,
}: Props) {
  const crits = alerts.filter((a) => a.severity === "critical").length;
  const warns = alerts.filter((a) => a.severity === "warning").length;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "10px 16px",
        background: "var(--bg-1)",
        borderBottom: "1px solid var(--line)",
        position: "sticky",
        top: 0,
        zIndex: 20,
      }}
    >
      <Logo />
      <div className="divider" />
      <div style={{ display: "flex", gap: 2 }}>
        {[
          { id: "overview", label: "Overview" },
          { id: "node", label: "Node drill-down" },
          { id: "job", label: "Job performance" },
        ].map((t) => (
          <button
            key={t.id}
            className={`nav-tab ${view === t.id ? "active" : ""}`}
            onClick={() => onView(t.id as typeof view)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div style={{ flex: 1 }} />
      {crumbs && (
        <div
          style={{
            fontSize: 11,
            color: "var(--fg-2)",
            fontFamily: "var(--mono)",
          }}
        >
          {crumbs}
        </div>
      )}
      <EnvPicker snapshot={snapshot} />
      <div className="divider" />
      <TimeRange value={timeRange} onChange={setTimeRange} />
      <RefreshIndicator status={status} />
      <div className="divider" />
      <button className="btn" onClick={onBell} style={{ position: "relative" }}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path
            d="M7 1C4.5 1 3 2.8 3 5V8L2 10H12L11 8V5C11 2.8 9.5 1 7 1ZM7 12C7.8 12 8.5 11.5 8.5 10.5H5.5C5.5 11.5 6.2 12 7 12Z"
            stroke="currentColor"
            strokeWidth="1.2"
            fill="none"
          />
        </svg>
        Alerts
        {crits > 0 && (
          <span
            className="chip crit pulse-ring"
            style={{ height: 16, padding: "0 5px", fontSize: 9 }}
          >
            {crits}
          </span>
        )}
        {crits === 0 && warns > 0 && (
          <span className="chip warn" style={{ height: 16, padding: "0 5px", fontSize: 9 }}>
            {warns}
          </span>
        )}
      </button>
      <button className="btn ghost" title="User">
        <div
          style={{
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: "linear-gradient(135deg, var(--violet), var(--cyan))",
            color: "#0a0f17",
            fontSize: 10,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          AK
        </div>
      </button>
    </div>
  );
}

function EnvPicker({ snapshot }: { snapshot: ClusterSnapshot | null }) {
  const [open, setOpen] = useState(false);
  const cluster = snapshot?.cluster_name ?? "rubin-prod";
  const region = snapshot?.cluster_region ?? "il-tlv-1";
  const nodeCount = snapshot?.nodes.length ?? 4;
  return (
    <div style={{ position: "relative" }}>
      <button className="btn" onClick={() => setOpen((o) => !o)} style={{ minWidth: 200 }}>
        <span className="chip ok" style={{ padding: "0 6px", height: 16 }}>
          <span className="dot" /> PROD
        </span>
        <span className="u-truncate">{cluster} · {region}</span>
        <span className="u-xdim">▾</span>
      </button>
      {open && (
        <div
          onMouseLeave={() => setOpen(false)}
          className="fade-in"
          style={{
            position: "absolute",
            top: 34,
            left: 0,
            background: "var(--bg-2)",
            border: "1px solid var(--line-2)",
            borderRadius: 6,
            minWidth: 240,
            zIndex: 30,
            boxShadow: "var(--shadow-2)",
          }}
        >
          {[
            { env: "PROD", name: `${cluster} · ${region}`, nodes: nodeCount, kind: "ok" },
            { env: "STAGE", name: "rubin-stage · il-tlv-1", nodes: 2, kind: "info" },
            { env: "LAB", name: "rubin-lab · il-haifa-1", nodes: 1, kind: "info" },
          ].map((o) => (
            <div
              key={o.name}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 10px",
                borderBottom: "1px solid var(--line)",
                cursor: "pointer",
              }}
            >
              <span className={`chip ${o.kind}`} style={{ height: 16 }}>
                <span className="dot" /> {o.env}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12 }}>{o.name}</div>
                <div
                  style={{
                    fontSize: 10,
                    color: "var(--fg-3)",
                    fontFamily: "var(--mono)",
                  }}
                >
                  {o.nodes} nodes
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TimeRange({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const opts = ["5m", "15m", "1h", "6h", "24h", "7d"];
  return (
    <div
      style={{
        display: "flex",
        gap: 2,
        background: "var(--bg-3)",
        borderRadius: 4,
        padding: 2,
        border: "1px solid var(--line-2)",
      }}
    >
      {opts.map((o) => (
        <button
          key={o}
          onClick={() => onChange(o)}
          className="btn xs ghost"
          style={{
            background: value === o ? "var(--bg-4)" : "transparent",
            color: value === o ? "var(--cyan)" : "var(--fg-2)",
            height: 20,
          }}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

function RefreshIndicator({ status }: { status: Status }) {
  const color =
    status === "live"
      ? "var(--cyan)"
      : status === "demo"
      ? "var(--violet)"
      : status === "polling"
      ? "var(--amber)"
      : status === "error"
      ? "var(--rose)"
      : "var(--fg-2)";
  const label =
    status === "live"
      ? "1s · live"
      : status === "demo"
      ? "1s · demo"
      : status === "polling"
      ? "1s · poll"
      : status === "connecting"
      ? "…"
      : "offline";
  return (
    <div
      className="btn xs"
      title={`Stream status: ${status}`}
      style={{ gap: 6 }}
    >
      <div
        className={status === "live" ? "pulse" : undefined}
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: color,
          boxShadow: `0 0 6px ${color}`,
        }}
      />
      <span className="mono">{label}</span>
    </div>
  );
}
